// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Every sound the game can make, declared once.
//
// `sfx.play()` on a cue no patch defines returns SILENTLY — it is not an error, it is nothing at
// all. So a mistyped cue name is an invisible bug that a suite of green tests will never mention,
// which is why `tests/source-hygiene.test.ts` greps every play() call in src/ and checks it against
// this list, and checks this list against the patches that actually exist.

import type { Patch } from '@ben-gy/game-engine/sound';

export const CUES = [
  'select',
  'waypoint',
  'stance',
  'commit',
  'countdown',
  'go',
  'fire',
  'kill',
  'hold',
  'win',
  'lose',
] as const;

export type Cue = (typeof CUES)[number];

/**
 * Procedural patches. Nothing here is an asset — the whole soundtrack is a few oscillator envelopes,
 * which is what keeps the game offline-capable and the bundle small.
 *
 * The mix is deliberately quiet under the replay: a five-second window can contain half a dozen
 * shots, and six loud noises in five seconds is not tension, it is a fire alarm.
 */
export const PATCHES: Record<string, Patch> = {
  select: { type: 'square', freq: [520, 520], dur: 0.04, gain: 0.1 },
  waypoint: { type: 'triangle', freq: [640, 780], dur: 0.05, gain: 0.09 },
  stance: { type: 'square', freq: [360, 420], dur: 0.05, gain: 0.09 },
  commit: { type: 'triangle', freq: [300, 540], dur: 0.16, gain: 0.16 },
  countdown: { type: 'sine', freq: [440, 440], dur: 0.1, gain: 0.16 },
  go: { type: 'sine', freq: [660, 990], dur: 0.22, gain: 0.2 },
  // A shot is short, dry and mid-range so several can land in one window without turning to mush.
  fire: { type: 'sawtooth', freq: [260, 120], dur: 0.07, gain: 0.13, noise: true },
  kill: { type: 'sawtooth', freq: [160, 60], dur: 0.3, gain: 0.2, noise: true },
  hold: { type: 'sine', freq: [560, 700], dur: 0.09, gain: 0.08 },
  win: { type: 'triangle', freq: [520, 980], dur: 0.5, gain: 0.22 },
  lose: { type: 'triangle', freq: [320, 140], dur: 0.5, gain: 0.2 },
};
